"use client"

import type React from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { api } from "../services/api"

// @ts-ignore
import { CowWidgetEventListeners, CowWidgetEvents } from '@cowprotocol/events'
// @ts-ignore
import { CowSwapWidget, TradeType, type CowSwapWidgetParams } from "@cowprotocol/widget-react"

import { AlertTriangle, ChevronDown, ExternalLink } from "lucide-react"
import "./CowSwap.css"

// Ethereum provider type definition that matches CowSwap's requirements
interface EthereumProvider {
  request: (args: { method: string; params?: any[] }) => Promise<any>
  on: (event: string, handler: (...args: any[]) => void) => void
  removeListener: (event: string, handler: (...args: any[]) => void) => void
  enable: () => Promise<string[]> // Required by CowSwap
  isMetaMask?: boolean
  isConnected?: () => boolean
}

// Network configuration
interface Network {
  id: number
  name: string
  icon: string
  color: string
  rpcUrl?: string
}

const networks: Network[] = [
  {
    id: 8453,
    name: "Base",
    icon: "https://assets.coingecko.com/asset_platforms/images/131/standard/base-network.png?1720533039",
    color: "#0052FF",
  },
  {
    id: 42161,
    name: "Arbitrum",
    icon: "https://assets.coingecko.com/coins/images/16547/standard/arb.jpg?1721358242",
    color: "#28A0F0",
  },
  {
    id: 1,
    name: "Ethereum",
    icon: "https://assets.coingecko.com/coins/images/279/standard/ethereum.png?1696501628",
    color: "#627EEA",
  },
]

const CowSwap: React.FC = () => {
  const [selectedNetwork, setSelectedNetwork] = useState<Network>(networks[0])
  const [isNetworkMenuOpen, setIsNetworkMenuOpen] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [currentWalletChainId, setCurrentWalletChainId] = useState<number | null>(null)
  const [showNetworkMismatch, setShowNetworkMismatch] = useState(false)
  const [connectedWalletAddress, setConnectedWalletAddress] = useState<string | null>(null)
  const [orderHash, setOrderHash] = useState<string | null>(null)

  // Refs for click-outside functionality
  const networkDropdownRef = useRef<HTMLDivElement>(null)
  const widgetContainerRef = useRef<HTMLDivElement>(null)

  // Get Ethereum provider with proper typing using type assertion
  const getProvider = useCallback((): EthereumProvider | undefined => {
    if (typeof window !== "undefined" && window.ethereum) {
      // Use type assertion to avoid conflicts with existing declarations
      return window.ethereum as unknown as EthereumProvider
    }
    return undefined
  }, [])

  const listeners: CowWidgetEventListeners = [
    {
      event: CowWidgetEvents.ON_TOAST_MESSAGE,
      handler: async (event) => {
        if (event.messageType === 'ORDER_FULFILLED') {
          // console.log('[ON_FULFILLED_ORDER]', event);
          // console.log(event.data)

          const re = /Traded\s+([0-9]*\.?[0-9]+)\s+([A-Za-z0-9]+)\s+for a total of\s+([0-9]*\.?[0-9]+)\s+([A-Za-z0-9]+)/;
          const m = event.message.match(re);
          if (!m) {
            throw new Error("Could not parse swap summary");
          }

          const amountIn   = parseFloat(m[1]);
          const tokenIn    = m[2];
          const amountOut  = parseFloat(m[3]);
          const tokenOut   = m[4];

          // fee = 0.25% of amountIn
          const fee        = amountIn * 0.0025;

          const payload = {
            network: selectedNetwork.name,
            amountIn: amountIn.toString(),
            tokenIn: tokenIn,
            amountOut: amountOut.toString(),
            tokenOut: tokenOut,
            fee: fee.toString(), 
            swapper: connectedWalletAddress,
            txHash: orderHash || event.data.orderUid
          }
          // console.log("PAYLOAD:")
          // console.log(payload)
          await api.post('/swap/', payload)
        }
        // if (event.messageType === 'ONCHAIN_TRANSACTION_MINED') {
        //   console.log(event.data.transactionHash)
        // }
      }
    }, {
      event: CowWidgetEvents.ON_ONCHAIN_TRANSACTION,
      // @ts-ignore
      handler: event => {
        console.log("TX MINED ONCHAIN")
        // console.log(event)
      }
    }, {
      event: CowWidgetEvents.ON_POSTED_ORDER,
      // @ts-ignore
      handler: (event) => {
        console.log("POSTED ORDER")
        // console.log(event)
        // setOrderHash(event.orderCreationHash?)
      }
    }, {
      event: CowWidgetEvents.ON_CANCELLED_ORDER,
      handler: () => {
        setOrderHash("")
      }
    }, {
      event:  CowWidgetEvents.ON_EXPIRED_ORDER,
      handler: () => {
        setOrderHash("")
      }
    }
  ]

  // Click outside handler for both network dropdown and widget
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close network dropdown if clicking outside
      if (networkDropdownRef.current && !networkDropdownRef.current.contains(event.target as Node)) {
        setIsNetworkMenuOpen(false)
      }

      // Close widget dropdowns if clicking outside the widget container
      if (widgetContainerRef.current && !widgetContainerRef.current.contains(event.target as Node)) {
        // Try to send message to widget to close any open dropdowns
        const widgetIframe = widgetContainerRef.current.querySelector("iframe")
        if (widgetIframe && widgetIframe.contentWindow) {
          try {
            widgetIframe.contentWindow.postMessage(
              {
                type: "CLOSE_DROPDOWNS",
                action: "close_all_dropdowns",
              },
              "*",
            )
          } catch (error) {
            // Silently handle cross-origin errors
            console.debug("Could not send close message to widget:", error)
          }
        }

        // Also try to trigger a click event on the widget to close dropdowns
        setTimeout(() => {
          if (widgetIframe) {
            try {
              // Simulate an escape key press which often closes dropdowns
              const escapeEvent = new KeyboardEvent("keydown", {
                key: "Escape",
                code: "Escape",
                keyCode: 27,
                which: 27,
                bubbles: true,
              })
              document.dispatchEvent(escapeEvent)
            } catch (error) {
              console.debug("Could not dispatch escape event:", error)
            }
          }
        }, 10)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Check wallet connection and network
  useEffect(() => {
    const checkWalletState = async () => {
      const provider = getProvider()
      if (!provider) return

      try {
        // Check if already connected
        const accounts = await provider.request({ method: "eth_accounts" })
        const isWalletConnected = accounts.length > 0
        setIsConnected(isWalletConnected)

        // Get current chain ID
        const chainId = await provider.request({ method: "eth_chainId" })
        const numericChainId = Number.parseInt(chainId, 16)
        setCurrentWalletChainId(numericChainId)

        // Set wallet address connected
        if (isWalletConnected) {
          setConnectedWalletAddress(accounts[0])
        } else {
          setConnectedWalletAddress(null)
        }

        // Check for network mismatch
        if (isWalletConnected && numericChainId !== selectedNetwork.id) {
          setShowNetworkMismatch(true)
        } else {
          setShowNetworkMismatch(false)
        }
      } catch (error) {
        console.error("Error checking wallet state:", error)
      }
    }

    checkWalletState()

    // Listen for account and network changes
    const provider = getProvider()
    if (provider) {
      const handleAccountsChanged = (accounts: string[]) => {
        setIsConnected(accounts.length > 0)
        setConnectedWalletAddress(accounts.length > 0 ? accounts[0] : null)
        if (accounts.length === 0) {
          setShowNetworkMismatch(false)
        }
      }

      const handleChainChanged = (chainId: string) => {
        const numericChainId = Number.parseInt(chainId, 16)
        setCurrentWalletChainId(numericChainId)

        // Check for network mismatch
        if (isConnected && numericChainId !== selectedNetwork.id) {
          setShowNetworkMismatch(true)
        } else {
          setShowNetworkMismatch(false)
        }
      }

      provider.on("accountsChanged", handleAccountsChanged)
      provider.on("chainChanged", handleChainChanged)

      return () => {
        provider.removeListener("accountsChanged", handleAccountsChanged)
        provider.removeListener("chainChanged", handleChainChanged)
      }
    }
  }, [getProvider, isConnected, selectedNetwork.id])

  // Switch network in wallet
  const switchWalletNetwork = async (network: Network): Promise<boolean> => {
    const provider = getProvider()
    if (!provider) {
      alert("Please install a Web3 wallet like MetaMask")
      return false
    }

    try {
      // Try to switch to the network
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${network.id.toString(16)}` }],
      })
      return true
    } catch (switchError: any) {
      // This error code indicates that the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        try {
          // Add the network to MetaMask
          const networkData = getNetworkData(network)
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [networkData],
          })
          return true
        } catch (addError) {
          console.error("Error adding network:", addError)
          return false
        }
      } else {
        console.error("Error switching network:", switchError)
        return false
      }
    }
  }

  // Get network data for adding to wallet
  const getNetworkData = (network: Network) => {
    const networkConfigs: { [key: number]: any } = {
      8453: {
        chainId: "0x2105",
        chainName: "Base",
        nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
        rpcUrls: ["https://mainnet.base.org"],
        blockExplorerUrls: ["https://basescan.org"],
      },
      42161: {
        chainId: "0xa4b1",
        chainName: "Arbitrum One",
        nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
        rpcUrls: ["https://arb1.arbitrum.io/rpc"],
        blockExplorerUrls: ["https://arbiscan.io"],
      },
      1: {
        chainId: "0x1",
        chainName: "Ethereum Mainnet",
        nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
        rpcUrls: ["https://mainnet.infura.io/v3/"],
        blockExplorerUrls: ["https://etherscan.io"],
      },
    }
    return networkConfigs[network.id]
  }

  // Handle network change
  const changeNetwork = async (network: Network) => {
    setSelectedNetwork(network)
    setIsNetworkMenuOpen(false)

    // If wallet is connected, try to switch the network in the wallet too
    if (isConnected) {
      const success = await switchWalletNetwork(network)
      if (!success) {
        // Show user that they need to manually switch
        setShowNetworkMismatch(true)
      }
    }
  }

  // Handle manual network switch
  const handleSwitchNetwork = async () => {
    const success = await switchWalletNetwork(selectedNetwork)
    if (success) {
      setShowNetworkMismatch(false)
    }
  }

  // Create widget parameters
  const getParams = (): CowSwapWidgetParams => {
    return {
      appCode: "Yuba",
      width: "100%",
      height: "580px", // Reduced height for compactness
      chainId: selectedNetwork.id,
      tokenLists: ["https://files.cow.fi/tokens/CowSwap.json", "https://files.cow.fi/tokens/CoinGecko.json"],
      tradeType: TradeType.SWAP,
      enabledTradeTypes: [TradeType.SWAP],
      theme: {
        baseTheme: "light",
        primary: "#67b7d1",
        background: "#f4f8f9",
        paper: "#ffffff",
        text: "#3c4b5a",
        danger: "#f28b82",
        alert: "#f7c6c7",
        warning: "#f59e0b",
        info: "#236a79",
        success: "#6abf69",
      },
      standaloneMode: true,
      disableToastMessages: true,
      disableProgressBar: false,
      hideBridgeInfo: false,
      hideOrdersTable: false,
      hideLogo: true,
      sounds: {
        orderExecuted: null,
        postOrder: null,
        orderError: null
      },
      partnerFee: {
        bps: 25,
        recipient: "0x17D471e2BE45DEf9DB7296B8896c9F61e51E60FD",
      },
      banners: {
        hideSafeWebAppBanner: true,
      },
    }
  }

  return (
    <div className="cow-swap-container">
      <div className="swap-header">
        <h1>Swap Tokens</h1>
        <p>Trade tokens across multiple networks with the best rates</p>
      </div>

      <div className="network-selector-container">
        <div className="network-selector" ref={networkDropdownRef}>
          <button className="network-dropdown-button" onClick={() => setIsNetworkMenuOpen(!isNetworkMenuOpen)}>
            <img src={selectedNetwork.icon || "/placeholder.svg"} alt={selectedNetwork.name} className="network-icon" />
            <span className="network-name">{selectedNetwork.name}</span>
            <ChevronDown className={`dropdown-icon ${isNetworkMenuOpen ? "open" : ""}`} />
          </button>

          {isNetworkMenuOpen && (
            <div className="network-dropdown">
              {networks.map((network) => (
                <button
                  key={network.id}
                  className={`network-option ${network.id === selectedNetwork.id ? "active" : ""}`}
                  onClick={() => changeNetwork(network)}
                >
                  <img src={network.icon || "/placeholder.svg"} alt={network.name} className="network-icon" />
                  <span>{network.name}</span>
                  {currentWalletChainId === network.id && isConnected && <span className="connected-indicator">●</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {isConnected && currentWalletChainId && (
          <div className="connection-status">
            <span className="status-dot">●</span>
            <span>{networks.find((n) => n.id === currentWalletChainId)?.name || "Unknown"} Connected</span>
          </div>
        )}
      </div>

      {/* Network Mismatch Warning */}
      {showNetworkMismatch && (
        <div className="network-mismatch-warning">
          <AlertTriangle className="warning-icon" />
          <div className="warning-content">
            <span>Network mismatch detected</span>
            <button onClick={handleSwitchNetwork} className="switch-network-button">
              Switch to {selectedNetwork.name}
            </button>
          </div>
        </div>
      )}

      <div className="widget-container" ref={widgetContainerRef}>
        {/* Use type assertion to satisfy TypeScript */}
        <CowSwapWidget params={getParams()} provider={getProvider() as any}  listeners={listeners} />
      </div>

      <div className="swap-footer">
        <div className="powered-by">
          <span>Powered by</span>
          <a href="https://cow.fi" target="_blank" rel="noopener noreferrer" className="cow-link">
            CoW Protocol <ExternalLink className="external-link-icon" />
          </a>
        </div>
      </div>
    </div>
  )
}

export default CowSwap
